import {testIdProps} from '@github-ui/test-id-props'
import {PulseIcon} from '@primer/octicons-react'
import {Box, Button, Link, Octicon, Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {memo, useCallback} from 'react'

import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import type {StatusUpdate} from '../../state-providers/status-updates/status-updates-context'
import {useStatusUpdates} from '../../state-providers/status-updates/status-updates-context'
import {StatusUpdatesResources} from '../../strings'
import {StatusUpdateEditor} from './status-update-editor'
import {StatusUpdateItem} from './status-update-item'

const statusUpdatesContainerStyle: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  flex: 'auto',
  borderTop: '1px solid',
  borderColor: 'border.muted',
  pt: 4,
  mt: 3,
  width: '100%',
}

const statusUpdatesHeadingStyle: BetterSystemStyleObject = {
  width: '100%',
  display: 'flex',
  minHeight: 32,
  justifyContent: 'space-between',
}

function StatusUpdatesHeader() {
  const {setIsAdding, statusUpdates} = useStatusUpdates()
  const {hasWritePermissions} = ViewerPrivileges()

  const hasUpdates = statusUpdates.length !== 0

  const handleOnClick = useCallback(() => {
    setIsAdding(true)
  }, [setIsAdding])

  if (hasUpdates) {
    return (
      <Box sx={statusUpdatesHeadingStyle}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
          <Octicon icon={PulseIcon} sx={{color: 'fg.subtle'}} />
          <Text as="h3" sx={{fontSize: 14, fontWeight: 'bold', color: 'fg.default'}}>
            {StatusUpdatesResources.title}
          </Text>
        </Box>
        {hasWritePermissions && (
          <Button variant="primary" onClick={handleOnClick} {...testIdProps('status-updates-create-button')}>
            {StatusUpdatesResources.addUpdateButtonText}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center',
        border: theme => `1px solid ${theme.colors.border.default}`,
        padding: '32px 16px',
        borderRadius: 2,
      }}
    >
      <Octicon icon={PulseIcon} size="medium" />
      <Text as="h3" sx={{fontSize: 20, fontWeight: 'bold', color: 'fg.default'}}>
        {StatusUpdatesResources.nullStateTitle}
      </Text>
      <Text sx={{fontSize: 14, color: 'fg.muted', textAlign: 'center'}}>{StatusUpdatesResources.description}</Text>
      <Box sx={{display: 'flex', alignItems: 'center', marginTop: 3, gap: 3}}>
        {hasWritePermissions && (
          <Button variant="primary" onClick={handleOnClick} {...testIdProps('status-updates-create-button')}>
            {StatusUpdatesResources.addUpdateButtonText}
          </Button>
        )}
        <Link
          href={'https://gh.io/status-updates-learn-more'}
          style={{cursor: 'pointer'}}
          tabIndex={0}
          target="_blank"
          {...testIdProps('status-updates-learn-more-link')}
        >
          {StatusUpdatesResources.learnMoreLinkText}
        </Link>
      </Box>
    </Box>
  )
}

export const StatusUpdates = memo(function SidePanelMemexStatusUpdates() {
  const {statusUpdates, addStatusUpdate, setIsAdding, isAdding} = useStatusUpdates()

  const handleCancel = () => {
    setIsAdding(false)
  }

  return (
    <Box {...testIdProps('status-updates-container')} sx={statusUpdatesContainerStyle}>
      {isAdding ? (
        <Box
          {...testIdProps('status-updates-create-container')}
          sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 3}}
        >
          <Text as="h3" sx={{fontSize: 14, fontWeight: 'bold', color: 'fg.default'}}>
            {StatusUpdatesResources.addUpdateTitle}
          </Text>

          <StatusUpdateEditor
            onCancel={handleCancel}
            onSave={({body, startDate, targetDate, status}: StatusUpdate) => {
              setIsAdding(false)

              addStatusUpdate({
                body,
                startDate,
                targetDate,
                status,
              })
            }}
          />
        </Box>
      ) : (
        <StatusUpdatesHeader />
      )}
      <Box as="ul" sx={{listStyleType: 'none'}} {...testIdProps('status-updates-item-list')}>
        {statusUpdates.map(statusUpdate => (
          <StatusUpdateItem key={statusUpdate.id} status={statusUpdate} />
        ))}
      </Box>
    </Box>
  )
})

try{ StatusUpdatesHeader.displayName ||= 'StatusUpdatesHeader' } catch {}
try{ StatusUpdates.displayName ||= 'StatusUpdates' } catch {}